/**
 * ===== VALIDATION SERVICE - SERVICE DE VALIDATION UNIFI\u00c9 =====
 * Service centralis\u00e9 pour toutes les validations;
 */
import { logger } from '../utils/ProfessionalLogger';
import { TemplateValidator } from '../utils/TemplateValidator';
import { UnitConverter, ConversionResult } from '../utils/UnitConverter';
import { DimensionValidator, DimensionConstraints } from '../utils/DimensionValidator';
export interface UnifiedValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  suggestions?: string[];
  data?: unknown;
}
export interface ValidationOptions {
  strictMode: boolean;
  allowCustomObjects: boolean;
  maxObjects: number;
  maxFileSize: number;
  precision: number;
}
export class ValidationService {
  private static instance: ValidationService;
  private templateValidator: TemplateValidator;
  private unitConverter: UnitConverter;
  private dimensionValidator: DimensionValidator;
  private options: ValidationOptions;
  private constructor() {
    this.templateValidator = new TemplateValidator();
    this.unitConverter = UnitConverter.getInstance();
    this.dimensionValidator = DimensionValidator.getInstance();
    this.options = {
      strictMode: false,
      allowCustomObjects: true,
      maxObjects: 1000,
      maxFileSize: 10 * 1024 * 1024,
      precision: 2,
    };
  }
  public static getInstance(): ValidationService {
    if (!ValidationService.instance) {
      ValidationService.instance = new ValidationService();
    }
    return ValidationService.instance;
  }
  /**
   * Valide un template complet;
   */
  public validateTemplate(template: unknown): UnifiedValidationResult {
    try {
      const result = this.templateValidator.validateTemplate(template);
      return {
        isValid: result.isValid,
        errors: result.errors,
        warnings: result.warnings,
        data: result.data,
      };
    } catch (error) {
      logger.error('Erreur lors de la validation du template:', error);
      return {
        isValid: false,
        errors: ['Erreur lors de la validation du template'],
        warnings: [],
        data: null,
      };
    }
  }
  /**
   * Valide les dimensions d'un template;
   */
  public validateTemplateDimensions(
    width: number,
    height: number,
    format?: string
  ): UnifiedValidationResult {
    try {
      let constraints: DimensionConstraints | undefined;
      if (format) {
        constraints = this.dimensionValidator.getFormatConstraints(format);
      }
      const result = this.dimensionValidator.validateDimensions(width, height, constraints);
      return {
        isValid: result.isValid,
        errors: result.errors,
        warnings: result.warnings,
        suggestions: result.suggestions,
      };
    } catch (error) {
      logger.error('Erreur lors de la validation des dimensions:', error);
      return {
        isValid: false,
        errors: ['Erreur lors de la validation des dimensions'],
        warnings: [],
        suggestions: [],
      };
    }
  }
  /**
   * Convertit des unit\u00e9s;
   */
  public convertUnits(
    value: number,
    fromUnit: string,
    toUnit: string,
    options?: Record<string, unknown>
  ): ConversionResult {
    try {
      return this.unitConverter.convertAuto(value, fromUnit, toUnit, options);
    } catch (error) {
      logger.error("Erreur lors de la conversion d'unités:", error);
      // Retourner une valeur par défaut en cas d'erreur
      return {
        value,
        unit: fromUnit,
        originalValue: value,
        originalUnit: fromUnit,
        precision: 2,
      };
    }
  }
  /**
   * Valide un fichier de template;
   */
  public validateTemplateFile(file: File): UnifiedValidationResult {
    try {
      const result = this.templateValidator.validateTemplateFile(file);
      return {
        isValid: result.isValid,
        errors: result.errors,
        warnings: result.warnings,
      };
    } catch (error) {
      logger.error('Erreur lors de la validation du fichier template:', error);
      return {
        isValid: false,
        errors: ['Erreur lors de la validation du fichier template'],
        warnings: [],
      };
    }
  }
  /**
   * Valide les contraintes d'un template;
   */
  public validateTemplateConstraints(
    template: unknown,
    constraints: {
      maxObjects?: number;
      maxFileSize?: number;
      allowedFormats?: string[];
      requiredProperties?: string[];
    }
  ): UnifiedValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    try {
      // CORRIGÉ: Caster template pour accéder à ses propriétés
      const typedTemplate = template as {
        data?: { objects?: unknown[] };
        format?: string;
        [key: string]: unknown;
      };

      // Validation du nombre d'objets;
      if (constraints.maxObjects && typedTemplate.data?.objects) {
        if (typedTemplate.data.objects.length > constraints.maxObjects) {
          errors.push(
            `Trop d'objets: ${typedTemplate.data.objects.length} (max: ${constraints.maxObjects})`
          );
        }
      }
      // Validation de la taille du fichier;
      if (constraints.maxFileSize) {
        const fileSize = JSON.stringify(template).length;
        if (fileSize > constraints.maxFileSize) {
          errors.push(
            `Fichier trop volumineux: ${this.formatBytes(fileSize)} (max: ${this.formatBytes(constraints.maxFileSize)})`
          );
        }
      }
      // Validation des formats autoris\u00e9s;
      if (constraints.allowedFormats && typedTemplate.format) {
        if (!constraints.allowedFormats.includes(typedTemplate.format)) {
          errors.push(`Format non autoris\u00e9: ${typedTemplate.format}`);
        }
      }
      // Validation des propri\u00e9t\u00e9s requises;
      if (constraints.requiredProperties) {
        constraints.requiredProperties.forEach(prop => {
          if (!typedTemplate[prop]) {
            errors.push(`Propri\u00e9t\u00e9 requise manquante: ${prop}`);
          }
        });
      }
      return {
        isValid: errors.length === 0,
        errors,
        warnings,
      };
    } catch (error) {
      logger.error('Erreur lors de la validation des contraintes:', error);
      return {
        isValid: false,
        errors: ['Erreur lors de la validation des contraintes'],
        warnings: [],
      };
    }
  }
  /**
   * Valide un objet de template;
   */
  public validateTemplateObject(obj: unknown): UnifiedValidationResult {
    try {
      const result = this.templateValidator.validateTemplateObject(obj);
      return {
        isValid: result.isValid,
        errors: result.errors,
        warnings: result.warnings,
        data: result.data,
      };
    } catch (error) {
      logger.error("Erreur lors de la validation de l'objet template:", error);
      return {
        isValid: false,
        errors: ["Erreur lors de la validation de l'objet template"],
        warnings: [],
        data: null,
      };
    }
  }
  /**
   * Valide les dimensions pour diff\u00e9rents formats;
   */
  public validateForFormat(
    width: number,
    height: number,
    format: 'social' | 'print' | 'web' | 'mobile' | 'video'
  ): UnifiedValidationResult {
    try {
      const result = this.dimensionValidator.validateForFormat(width, height, format);
      return {
        isValid: result.isValid,
        errors: result.errors,
        warnings: result.warnings,
        suggestions: result.suggestions,
      };
    } catch (error) {
      logger.error('Erreur lors de la validation du format:', error);
      return {
        isValid: false,
        errors: ['Erreur lors de la validation du format'],
        warnings: [],
        suggestions: [],
      };
    }
  }
  /**
   * Valide un aspect ratio;
   */
  public validateAspectRatio(
    width: number,
    height: number,
    constraints?: DimensionConstraints
  ): UnifiedValidationResult {
    try {
      const result = this.dimensionValidator.validateAspectRatio(width, height, constraints);
      return {
        isValid: result.isValid,
        errors: result.errors,
        warnings: result.warnings,
        suggestions: result.suggestions,
      };
    } catch (error) {
      logger.error("Erreur lors de la validation de l'aspect ratio:", error);
      return {
        isValid: false,
        errors: ["Erreur lors de la validation de l'aspect ratio"],
        warnings: [],
        suggestions: [],
      };
    }
  }
  /**
   * Valide une couleur;
   */
  public validateColor(color: string): UnifiedValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    try {
      // Validation de base;
      if (!color || typeof color !== 'string') {
        errors.push('Couleur invalide');
        return { isValid: false, errors, warnings };
      }
      // Validation du format hex;
      if (color.startsWith('#')) {
        if (!/^#[0-9A-Fa-f]{6}$/.test(color)) {
          errors.push('Format hex invalide');
        }
      }
      // Validation du format RGB;
      else if (color.startsWith('rgb')) {
        if (!/^rgb\(\d+,\s*\d+,\s*\d+\)$/.test(color)) {
          errors.push('Format RGB invalide');
        }
      }
      // Validation du format RGBA;
      else if (color.startsWith('rgba')) {
        if (!/^rgba\(\d+,\s*\d+,\s*\d+,\s*[\d.]+\)$/.test(color)) {
          errors.push('Format RGBA invalide');
        }
      }
      // Validation des noms de couleurs;
      else {
        const validColors = [
          'red',
          'green',
          'blue',
          'yellow',
          'orange',
          'purple',
          'pink',
          'brown',
          'black',
          'white',
          'gray',
          'grey',
          'cyan',
          'magenta',
          'lime',
          'navy',
          'teal',
          'olive',
          'maroon',
          'silver',
        ];
        if (!validColors.includes(color.toLowerCase())) {
          warnings.push('Nom de couleur non standard');
        }
      }
      return {
        isValid: errors.length === 0,
        errors,
        warnings,
      };
    } catch (error) {
      logger.error('Erreur lors de la validation de la couleur:', error);
      return {
        isValid: false,
        errors: ['Erreur lors de la validation de la couleur'],
        warnings: [],
      };
    }
  }
  /**
   * Valide un nom de fichier;
   */
  public validateFileName(fileName: string): UnifiedValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    try {
      // Validation de base;
      if (!fileName || typeof fileName !== 'string') {
        errors.push('Nom de fichier invalide');
        return { isValid: false, errors, warnings };
      }
      // Validation de la longueur;
      if (fileName.length > 255) {
        errors.push('Nom de fichier trop long');
      }
      // Validation des caract\u00e8res interdits;
      const forbiddenChars = /[<>:"/\\|?*]/;
      if (forbiddenChars.test(fileName)) {
        errors.push('Caract\u00e8res interdits dans le nom de fichier');
      }
      // Validation de l'extension;
      const extension = fileName.split('.').pop()?.toLowerCase();
      const allowedExtensions = ['json', 'template', 'png', 'jpg', 'jpeg', 'svg', 'pdf'];
      if (!extension || !allowedExtensions.includes(extension)) {
        warnings.push(`Extension non standard: .${extension}`);
      }
      return {
        isValid: errors.length === 0,
        errors,
        warnings,
      };
    } catch (error) {
      logger.error('Erreur lors de la validation du nom de fichier:', error);
      return {
        isValid: false,
        errors: ['Erreur lors de la validation du nom de fichier'],
        warnings: [],
      };
    }
  }
  /**
   * Configure les options de validation;
   */
  public configure(options: Partial<ValidationOptions>): void {
    this.options = { ...this.options, ...options };
    this.templateValidator = new TemplateValidator({
      strictMode: this.options.strictMode,
      allowCustomObjects: this.options.allowCustomObjects,
      maxObjects: this.options.maxObjects,
      maxFileSize: this.options.maxFileSize,
    });
    this.dimensionValidator.configure({
      strictMode: this.options.strictMode,
      allowZero: true,
    });
  }
  /**
   * Obtient les options actuelles;
   */
  public getOptions(): ValidationOptions {
    return { ...this.options };
  }
  /**
   * Formate les bytes en format lisible;
   */
  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }
  /**
   * Nettoie les ressources;
   */
  public destroy(): void {
    this.templateValidator.destroy();
    this.unitConverter.destroy();
    this.dimensionValidator.destroy();
  }
}
